import { CheckCircle2, XCircle, RotateCcw, Trash2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { COLLECTION_LABELS } from '../constants';
import type { ExecuteResponse } from '../types';

interface MigrationResultProps {
  result: ExecuteResponse;
  onReset: () => void;
  onCleanup?: () => void;
  isCleaningUp?: boolean;
  cleanupDone?: boolean;
}

export function MigrationResult({ result, onReset, onCleanup, isCleaningUp, cleanupDone }: MigrationResultProps) {
  const isSuccess = result.status;
  const successCount = result.results.filter((r) => r.status === 'success').length;
  const errorCount = result.results.filter((r) => r.status === 'error').length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          {isSuccess ? (
            <CheckCircle2 className="h-6 w-6 text-green-600" />
          ) : (
            <XCircle className="h-6 w-6 text-destructive" />
          )}
          <CardTitle>
            {isSuccess ? 'Migration Completed' : 'Migration Failed'}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary */}
        <div className="flex flex-wrap gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Total migrated:</span>{' '}
            <span className="font-medium">{result.totalMigrated.toLocaleString()}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Duration:</span>{' '}
            <span className="font-medium">{(result.duration / 1000).toFixed(1)}s</span>
          </div>
          <div>
            <span className="text-muted-foreground">Mode:</span>{' '}
            <Badge variant="outline">{result.mode}</Badge>
          </div>
          {result.sourceArchived && (
            <div>
              <Badge variant="secondary">Source archived</Badge>
            </div>
          )}
          {result.mode === 'copy' && result.batchId && (
            <div>
              <span className="text-muted-foreground">Batch ID:</span>{' '}
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{result.batchId}</code>
            </div>
          )}
        </div>

        {/* Errors */}
        {result.errors.length > 0 && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3">
            <p className="text-sm font-medium text-destructive mb-1">Errors:</p>
            <ul className="list-disc pl-6 text-sm text-destructive">
              {result.errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Per-collection results */}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Collection</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Count</TableHead>
              <TableHead>Error</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {result.results.map((r) => (
              <TableRow key={r.collection}>
                <TableCell className="font-medium">
                  {COLLECTION_LABELS[r.collection] || r.collection}
                </TableCell>
                <TableCell>
                  <Badge
                    variant={
                      r.status === 'success'
                        ? 'default'
                        : r.status === 'error'
                          ? 'destructive'
                          : 'secondary'
                    }
                  >
                    {r.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">{(r.count ?? 0).toLocaleString()}</TableCell>
                <TableCell className="text-sm text-destructive">{r.error || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Summary badges */}
        <div className="flex items-center gap-2 text-sm">
          <Badge variant="default">{successCount} succeeded</Badge>
          {errorCount > 0 && <Badge variant="destructive">{errorCount} failed</Badge>}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2 border-t">
          <Button onClick={onReset} variant="outline">
            <RotateCcw className="mr-2 h-4 w-4" />
            Start New Migration
          </Button>
          {result.mode === 'copy' && result.batchId && onCleanup && (
            <Button
              onClick={() => {
                if (window.confirm('This will delete all documents created by this copy operation. Continue?')) {
                  onCleanup();
                }
              }}
              variant={cleanupDone ? 'outline' : isSuccess ? 'outline' : 'destructive'}
              disabled={isCleaningUp || cleanupDone}
            >
              {isCleaningUp ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : cleanupDone ? (
                <CheckCircle2 className="mr-2 h-4 w-4 text-green-600" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              {isCleaningUp
                ? 'Cleaning up...'
                : cleanupDone
                  ? 'Cleanup Complete'
                  : isSuccess
                    ? 'Undo Copy'
                    : 'Cleanup Copied Documents'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
